/**
 * This function was adapted from the one in the ReadMe of react-easy-crop
 */
export const createImage = (url) =>
  new Promise((resolve, reject) => {
    const image = new Image();
    image.addEventListener("load", () => resolve(image));
    image.addEventListener("error", (error) => reject(error));
    image.setAttribute("crossOrigin", "anonymous"); // needed to avoid cross-origin issues on CodeSandbox
    image.src = url;
  });

export function getRadianAngle(degreeValue) {
  return (degreeValue * Math.PI) / 180;
}

/**
 * Returns the new bounding area of a rotated rectangle.
 */
export function rotateSize(width, height, rotation) {
  const rotRad = getRadianAngle(rotation);

  return {
    width:
      Math.abs(Math.cos(rotRad) * width) + Math.abs(Math.sin(rotRad) * height),
    height:
      Math.abs(Math.sin(rotRad) * width) + Math.abs(Math.cos(rotRad) * height),
  };
}

/**
 * Image Quality Configurations
 * Different use cases require different quality settings
 */
export const IMAGE_CONFIGS = {
  // High quality for important images (fundraisers, events)
  HIGH_QUALITY: {
    maxWidth: 1920,
    maxHeight: 1080,
    quality: 0.85,  // 85% quality
    description: 'Best for main images, hero sections'
  },
  // Standard quality for general use
  STANDARD: {
    maxWidth: 1280,
    maxHeight: 720,
    quality: 0.80,  // 80% quality
    description: 'Good balance of quality and file size'
  },
  // Optimized for smaller file sizes
  OPTIMIZED: {
    maxWidth: 1024,
    maxHeight: 768,
    quality: 0.75,  // 75% quality
    description: 'Optimized for faster loading'
  }
};

/**
 * Calculate dimensions to fit within max size while maintaining aspect ratio
 */
function getResizedDimensions(width, height, maxWidth, maxHeight) {
  if (width <= maxWidth && height <= maxHeight) {
    return { width, height };
  }

  const aspectRatio = width / height;

  if (width > maxWidth) {
    width = maxWidth;
    height = Math.round(width / aspectRatio);
  }

  if (height > maxHeight) {
    height = maxHeight;
    width = Math.round(height * aspectRatio);
  }

  return { width, height };
}

/**
 * This function was adapted from the one in the ReadMe of react-easy-crop
 *
 * @param {string} imageSrc - Image source URL
 * @param {object} pixelCrop - Crop dimensions
 * @param {number} rotation - Rotation angle
 * @param {object} flip - Flip options
 * @param {object} config - Image quality configuration (defaults to HIGH_QUALITY)
 * @param {string} outputFormat - Output format: 'image/jpeg' or 'image/png' (defaults to 'image/jpeg')
 */
export default async function getCroppedImg(
  imageSrc,
  pixelCrop,
  rotation = 0,
  flip = { horizontal: false, vertical: false },
  config = IMAGE_CONFIGS.HIGH_QUALITY,
  outputFormat = 'image/jpeg'
) {
  const image = await createImage(imageSrc);
  const canvas = document.createElement("canvas");
  const ctx = canvas.getContext("2d");

  if (!ctx) {
    return null;
  }

  const rotRad = getRadianAngle(rotation);

  // calculate bounding box of the rotated image
  const { width: bBoxWidth, height: bBoxHeight } = rotateSize(
    image.width,
    image.height,
    rotation
  );

  // set canvas size to match the bounding box
  canvas.width = bBoxWidth;
  canvas.height = bBoxHeight;

  // translate canvas context to a central location to allow rotating and flipping around the center
  ctx.translate(bBoxWidth / 2, bBoxHeight / 2);
  ctx.rotate(rotRad);
  ctx.scale(flip.horizontal ? -1 : 1, flip.vertical ? -1 : 1);
  ctx.translate(-image.width / 2, -image.height / 2);

  // draw rotated image
  ctx.drawImage(image, 0, 0);

  const croppedCanvas = document.createElement("canvas");
  const croppedCtx = croppedCanvas.getContext("2d");

  if (!croppedCtx) {
    return null;
  }

  // Calculate optimal dimensions using provided config
  const { width: resizedWidth, height: resizedHeight } = getResizedDimensions(
    pixelCrop.width,
    pixelCrop.height,
    config.maxWidth,
    config.maxHeight
  );

  // Set the size of the cropped canvas to the resized dimensions
  croppedCanvas.width = resizedWidth;
  croppedCanvas.height = resizedHeight;

  // Draw the cropped and resized image onto the new canvas
  croppedCtx.drawImage(
    canvas,
    pixelCrop.x,
    pixelCrop.y,
    pixelCrop.width,
    pixelCrop.height,
    0,
    0,
    resizedWidth,  // Scale to resized width
    resizedHeight  // Scale to resized height
  );

  // As Base64 string with configurable compression
  // Uses quality setting from config (default: 0.85 for HIGH_QUALITY)
  // PNG format ignores quality parameter but preserves transparency
  return croppedCanvas.toDataURL(outputFormat, config.quality);

  // As a blob
  // return new Promise((resolve, reject) => {
  //   croppedCanvas.toBlob((file) => {
  //     resolve(URL.createObjectURL(file))
  //   }, 'image/jpeg')
  // })
}
